<template>
  <Component
    :is="isValidDownload ? SharedBaseDownload : SharedBaseLink"
    v-if="isValidDownload || isValidUrl || hasOverlayLayer || $attrs?.['onClick']"
    v-editable="blok"
    :link="isValidDownload ? undefined : blok.link"
    :asset="isValidDownload ? (asset ?? undefined) : undefined"
    :filename="isValidDownload ? blok.text : undefined"
    class="util-link-item-icon"
    :class="[isDark ? 'is-dark' : 'is-light', { 'is-disabled': isDisabled }]"
    @click="onButtonClick"
    @mouseenter="onIconMouseOver"
    @mouseleave="onIconMouseOut">
    <BaseIcon v-if="icon && iconPosition === 'start'" :name="icon" :size="16" color="inherit" class="icon-start" />
    <div v-if="hasText" class="text">
      <span class="main-text">
        <span>{{ blok.text }}</span>
        <BaseIcon v-if="icon && iconPosition === 'end'" :name="icon" :size="16" color="inherit" class="icon-end" />
      </span>
      <span v-if="subtext" class="subtext" :class="isDark ? 'is-dark' : 'is-light'">
        {{ subtext }}
      </span>
    </div>
  </Component>
</template>

<script lang="ts" setup>
import { computed } from 'vue';
import { SharedBaseDownload, SharedBaseLink } from '@seb-platform/shared/components/base';
import { useIconAnimation, useLayer } from '@seb-platform/shared/composables';
import { urlExtractFromLink, urlIsExternal } from '@seb-platform/shared/utils/url';
import { BaseIcon } from '~/components/base';
import type { AssetStoryblok, UtilLinkOverlayItemStoryblok } from '~/types/storyblok-generated';
import {
  ICON_EXTERNAL,
  ICON_FACEBOOK,
  ICON_INSTAGRAM,
  ICON_INTERNAL,
  ICON_LINKEDIN,
  ICON_MAIL,
  ICON_OVERLAY,
  ICON_PHONE,
  ICON_VIMEO,
  ICON_XING,
  ICON_YOUTUBE,
} from '~/utils/icon';

const props = defineProps<{
  blok: UtilLinkOverlayItemStoryblok;
  forceIcon?: string;
  forceIconPosition?: 'start' | 'end';
  isDark?: boolean;
  isDisabled?: boolean;
  subtext?: string;
  asset?: AssetStoryblok | null;
  detectIcon?: boolean;
  hideInternalIcon?: boolean;
}>();

const { onIconMouseOut, onIconMouseOver } = useIconAnimation();
const hasText = computed(() => !!props.blok.text);
const isValidDownload = computed(() => !!props.asset?.filename);
const isValidUrl = computed(() => !!props.blok.link && !!urlExtractFromLink(props.blok.link));
const hasOverlayLayer = computed(() => !!props.blok.item_overlay && typeof props.blok.item_overlay !== 'string');

const icon = computed(() => {
  if (props.detectIcon) {
    if (props.blok.link?.url.includes('tel:')) {
      return ICON_PHONE;
    }

    if (props.blok.link?.url.includes('youtube')) {
      return ICON_YOUTUBE;
    }

    if (props.blok.link?.url.includes('mailto:')) {
      return ICON_MAIL;
    }

    if (props.blok.link?.url.includes('linkedin')) {
      return ICON_LINKEDIN;
    }

    if (props.blok.link?.url.includes('facebook')) {
      return ICON_FACEBOOK;
    }

    if (props.blok.link?.url.includes('vimeo')) {
      return ICON_VIMEO;
    }

    if (props.blok.link?.url.includes('insta')) {
      return ICON_INSTAGRAM;
    }

    if (props.blok.link?.url.includes('xing')) {
      return ICON_XING;
    }
  }

  if (props.forceIcon) {
    return props.forceIcon;
  }
  if (hasOverlayLayer.value) {
    return ICON_OVERLAY;
  }
  if (props.blok.link?.linktype === 'url' && urlIsExternal(props.blok.link.url)) {
    return ICON_EXTERNAL;
  } else if (!props.hideInternalIcon) {
    return ICON_INTERNAL;
  }

  return false;
});
const iconPosition = computed(() => {
  if (props.detectIcon && props.blok.link && !urlIsExternal(props.blok.link.url)) {
    return 'start';
  }

  if (props.forceIconPosition) {
    return props.forceIconPosition;
  }
  return hasOverlayLayer.value ? 'start' : 'end';
});

function onButtonClick() {
  if (!props.blok.item_overlay || typeof props.blok.item_overlay === 'string') {
    return;
  }

  // Open overlay
  const { state } = useLayer();
  state.corePageLayerId = props.blok.item_overlay.uuid;
}
</script>

<style src="./UtilLinkItemIcon.scss" lang="scss" scoped />
